import { createSelector } from '@reduxjs/toolkit';
import dayjs from 'dayjs';

import { selectSelectedExpertId, selectSelectedType, selectAdditionalSelectedExpertIds } from './bookingSelectors';
import { selectAllExperts } from 'FRONTEND/reducers/expertSlice';
import { selectAllSlots } from 'FRONTEND/reducers/slotSlice';
import { selectUserTenantIds } from 'FRONTEND/reducers/userSlice';
import { VIDEO_SPECIALIST_ASSESSMENT, FormTypes } from 'FRONTEND/utils/constants';

export const selectAllMatchingExperts = (state) => {
  return state.expert.matchingIdList
    .map((id) => state.expert.entities[id])
    .filter(Boolean);
};
export const selectAdditionalSelectedExperts = createSelector(
  (state) => state.expert.entities,
  selectAdditionalSelectedExpertIds,
  (expertMap, additionalSelectedIdList) => additionalSelectedIdList
    .map((id) => expertMap[id])
    .filter(Boolean),
);

/**
 * Selector to get unselected experts based on the provided criteria.
 *
 * @param {Object} state - The Redux store state.
 * @returns {Array} - An array of unselected experts filtered based on the criteria.
 */
export const selectUnselectedMatchingExperts = createSelector(
  selectAllMatchingExperts,
  selectAdditionalSelectedExpertIds,
  selectSelectedExpertId,
  selectSelectedType,
  (state) => state.user.id,
  (state) => state.booking.formType === FormTypes.UNRESTRICTED_FORM,
  (
    matchingExpertList,
    additionalSelectedIdList,
    selectedExpertId,
    selectedType,
    currentUserId,
    isUnrestricted,
  ) => {
    return matchingExpertList.filter(({ id }) => {
    // Check if the expert is not already selected and is not the currently selected expert.
      const isNotSelected = !additionalSelectedIdList.includes(id) && id !== selectedExpertId;

      // If the appointment type is 'telestaff', only check if the expert is not selected.
      if (selectedType === VIDEO_SPECIALIST_ASSESSMENT && !isUnrestricted) {
        return isNotSelected;
      }

      // Otherwise, also ensure the expert is not the current user.
      return isNotSelected && id !== currentUserId;
    });
  },
);

const buildMatchingRegExp = (matchString) => new RegExp(matchString.toLowerCase().split(' ').join('.*'));
export const selectFilteredManagedExperts = createSelector(
  selectAllExperts,
  (state) => state.expert.managedIdList,
  (state) => state.expert.filterText,
  (state) => state.expert.checkedList,
  (expertList, managedIdList, filterText, checkedList) => {
    let filteredExpertList;
    if (filterText) {
      const matchingRegExp = buildMatchingRegExp(filterText);
      filteredExpertList = expertList.filter((expert) => managedIdList.includes(expert.id) && expert.alias?.toLowerCase().match(matchingRegExp));
    } else {
      filteredExpertList = expertList.filter(({ id }) => managedIdList.includes(id));
    }
    return filteredExpertList.sort((a, b) => {
      const aIsChecked = checkedList.includes(a.id);
      const bIsChecked = checkedList.includes(b.id);
      if (aIsChecked && !bIsChecked) {
        return -1;
      }
      return 1;
    });
  });

export const unfilteredManagedExpertCount = createSelector(
  (state) => state.expert.managedIdList,
  (managedExpertIds) => managedExpertIds.length,
);
export const selectManagedExpertIds = (state) => state.expert.managedIdList;

export const selectSlotAvailableExperts = createSelector(
  selectAllExperts,
  selectAllSlots,
  (state) => state.booking.selectedSlot,
  (state) => state.booking.hasPayment,
  (allExperts, allSlots, selectedSlot, hasPayment) => {
    return allExperts
      .filter((expert) => {
        // Slot filtering logic
        return allSlots.some(
          (slot) =>
            slot.actorId === expert.id &&
    dayjs(selectedSlot?.startDate).isBetween(slot.start, slot.end, 'minute', '[)'),
        );
      });
  },
);

// Check if selected expert has a connected Stripe account
export const selectedExpertHasStripe = createSelector(
  (state, expertID) => expertID || selectSelectedExpertId(state),
  (state) => selectAllExperts(state),
  (selectedExpertId, allExperts) => {
    const expert = allExperts.find((expert) => expert.id === selectedExpertId);
    return expert?.stripe?.id && expert?.stripe?.id !== 'None' && expert?.stripe?.status !== 'PENDING';
  },
);


export const selectUserAndSelectedExpertsCommonTenantIds = createSelector(
  selectAdditionalSelectedExperts,
  selectUserTenantIds,
  (additionalSelectedExperts, userTenantIds) => {
    if (!additionalSelectedExperts.length) {
      return userTenantIds;
    } else {
      const finalTenantIds = [];
      additionalSelectedExperts?.forEach(({ tenantList }) => {
        tenantList?.forEach((id) => {
          if (userTenantIds.includes(id)) {
            finalTenantIds.push(id);
          }
        });
      });
      return finalTenantIds;
    }
  },
);
